<template>
  <auth-container>
    <jz-spinner v-if="processing" />

    <auth-card v-else-if="jwtValidationError" id="password-reset-card" title="">
      <template #content>
        <auth-error>{{ jwtValidationError | __ }}</auth-error>

        <jz-container id="login-link">
          ログインは
          <router-link :to="{ name: loginRouteName }">こちら</router-link>
        </jz-container>
      </template>
    </auth-card>

    <auth-card v-else id="password-reset-card" :title="'user.authentication.invitation.title' | __">
      <template #content>
        <auth-instructions>{{
          "user.authentication.invitation.instructions" | __
        }}</auth-instructions>

        <jz-container class="invitation-details">
          <jz-container v-if="orgName" class="invitation-details-row">
            <span class="label">{{ "user.authentication.invitation.org" | __ }}</span>
            <span class="value">{{ orgName }}</span>
          </jz-container>

          <jz-container v-if="userName" class="invitation-details-row">
            <span class="label">{{ "user.authentication.invitation.user" | __ }}</span>
            <span class="value">{{ userName }}</span>
          </jz-container>
        </jz-container>

        <auth-error v-if="error" class="error">{{ errorMessage }}</auth-error>

        <form id="password-reset-form" @submit.prevent="submitPasswords">
          <jz-container class="form-content">
            <input autocomplete="username" :value="email" hidden />

            <password-input-with-tooltip
              autocomplete-type="new-password"
              v-model="password"
              class="pwd-reset-card-input"
              name="password"
              :placeholder="'user.authentication.invitation.password' | __"
              type="password"
            />

            <password-confirmation-with-tooltip
              autocomplete-type="new-password"
              v-model="passwordConfirmation"
              class="pwd-reset-card-input"
              name="passwordConfirmation"
              :placeholder="'user.authentication.invitation.passwordConfirmation' | __"
              type="password"
            />
          </jz-container>

          <jz-button-group>
            <jz-button :disabled="buttonDisabled" primary rounded type="submit">{{
              "common.ok" | __
            }}</jz-button>
          </jz-button-group>
        </form>
      </template>
    </auth-card>
  </auth-container>
</template>

<script>
import { mapState } from "vuex";
import AuthCard from "@tt-app/components/user/auth/AuthCard.vue";
import PasswordInputWithTooltip from "@tt-app/components/user/auth/PasswordInputWithTooltip.vue";
import PasswordConfirmationWithTooltip from "@tt-app/components/user/auth/PasswordConfirmationWithTooltip";
import AuthContainer from "@tt-app/components/user/auth/AuthContainer";
import AuthError from "@tt-app/components/user/auth/AuthError";
import AuthInstructions from "@tt-app/components/user/auth/AuthInstructions";
import loginIcon from "@joyz/app-core/assets/icons/lightblue/LogIn.svg";
import { VUEX_FORM_NAMESPACE, VUEX_FORM_MUTATION_NAMES } from "@tt-app/constants/form";
import { ROUTES } from "@tt-app/constants/router";
import {
  VUEX_USER_ACTION_NAMES,
  VUEX_USER_MUTATION_NAMES,
  VUEX_USER_NAMESPACE
} from "@tt-app/constants";

const {
  HOME,
  USER: {
    AUTHENTICATION: { LOGIN }
  }
} = ROUTES;

const { LOGIN_FAILURE } = VUEX_USER_MUTATION_NAMES;
const {
  SET_PASSWORD_WITH_TMP_PASSWORD_REQUEST,
  SET_PASSWORD_WITH_TOKEN_REQUEST
} = VUEX_USER_ACTION_NAMES;
const { UPDATE_FORM } = VUEX_FORM_MUTATION_NAMES;

// TODO: seems like this could be broken into multiple components rather than using so many conditionals
export default {
  name: "PwdChangeFromInvitation",
  components: {
    AuthCard,
    AuthContainer,
    AuthError,
    AuthInstructions,
    PasswordConfirmationWithTooltip,
    PasswordInputWithTooltip
  },
  data() {
    return {
      password: "",
      passwordConfirmation: "",
      processing: false,
      jwt: this.$router.currentRoute.query.token
    };
  },
  computed: {
    ...mapState({
      email: state => state.user.email,
      error: state => state.user.authentication.error,
      groupNames: state => state.user.groupNames,
      jwtValidationError: state => state.user.authentication.jwtValidationError,
      orgName: state => state.user.orgName,
      token: state => state.user.authentication.token,
      userName: state => state.user.userName
    }),
    buttonDisabled() {
      const passwordsPresent = this.password !== "" && this.passwordConfirmation !== "";
      return !passwordsPresent || !this.passwordsMatch() || this.processing;
    },
    errorMessage() {
      const translatedMessage = this.$__(this.error);
      return translatedMessage || this.error;
    }
  },
  watch: {
    password() {
      this.comparePasswords();
    },
    passwordConfirmation() {
      this.comparePasswords();
    },
    token() {
      this.checkAuthenticationStatus();
    }
  },
  mounted() {
    this.checkAuthenticationStatus();
  },
  created() {
    this.loginIcon = loginIcon;
    this.loginRouteName = LOGIN.NAME;
    this.validateJwt();
  },
  methods: {
    checkAuthenticationStatus() {
      if (this.token) {
        const nextPage = this.$route.query.nextPage || HOME.NAME;
        this.$router.push({ name: nextPage });
      }
    },
    comparePasswords() {
      if (this.password && this.passwordConfirmation && !this.passwordsMatch()) {
        this.$store.commit(
          `${VUEX_USER_NAMESPACE}/${LOGIN_FAILURE}`,
          "user.authentication.error.passwords.differ"
        );
      } else {
        this.$store.commit(`${VUEX_USER_NAMESPACE}/${LOGIN_FAILURE}`, "");
      }
    },
    passwordsMatch() {
      return this.password === this.passwordConfirmation;
    },
    async submitPasswords() {
      this.processing = true;
      try {
        if (this.jwt) {
          await this.$store.dispatch(`${VUEX_USER_NAMESPACE}/${SET_PASSWORD_WITH_TOKEN_REQUEST}`, {
            token: this.jwt,
            password: this.password
          });
        } else {
          await this.$store.dispatch(
            `${VUEX_USER_NAMESPACE}/${SET_PASSWORD_WITH_TMP_PASSWORD_REQUEST}`,
            {
              password: this.password
            }
          );
        }
      } catch (error) {
        console.log({ error });
      }
      this.processing = false;
    },
    updateFormStore(event) {
      const formDetails = {
        formName: event.target.dataset.formName,
        field: event.target.name,
        value: event.target.value
      };
      this.$store.commit(`${VUEX_FORM_NAMESPACE}/${UPDATE_FORM}`, formDetails);
      this.comparePasswords();
    },
    async validateJwt() {
      this.processing = true;
      if (this.jwt) {
        await this.$store.dispatch(
          `${VUEX_USER_NAMESPACE}/${VUEX_USER_ACTION_NAMES.VALIDATE_JWT_REQUEST}`,
          { jwt: this.jwt }
        );
      }
      this.processing = false;
    }
  }
};
</script>

<style lang="scss">
#password-reset-card {
  .invitation-details {
    flex-direction: column;
    margin-bottom: $gutter;

    .invitation-details-row {
      font-size: $font-size-sm;
      font-weight: bold;

      .label {
        @include is-text-description($font-size-sm);
      }
      .value {
        color: var(--color-dark);
      }
    }
  }

  .jz-button-group {
    padding: ($gutter * 1.5) 0 $gutter;
  }

  #login-link {
    padding-bottom: 24px;
    flex-direction: column;
    color: $color-outer-space;
    font-size: $font-size-xs;
    margin-top: $gutter / 2;
    text-align: center;
  }
}
</style>
